import React, { useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronRight, ChevronDown, Home } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useRootLabel } from '@/stores/scope';
import { useScopePinStore } from '@/stores/scope-pin';
import { KpiCard } from '@/components/KpiCard';
import { formatCurrency, formatPercent, formatCount } from '@/lib/format';
import { visitorCountSourceBadge } from '@/lib/visitor-count-labels';
import { PublishGateBanner } from '@/components/publish/PublishGateBanner';
import { AiPeriodCommentPanel } from '@/components/publish/AiPeriodCommentPanel';
import { DashboardNodeMemo } from '@/components/publish/DashboardNodeMemo';
import type { PublishGateInfo } from '@/lib/types-publish-gate';

/** kpi_threshold_master の kpi_type をキーとした閾値+警告フラグ (DB enum 値をそのままキーに使用) */
type ThresholdEntry = { value: number; direction: 'above' | 'below'; alert: boolean };

type StoreKpi = {
  store_id: string;
  store_name: string;
  sales_tax_excluded: number;
  store_sales_tax_excluded: number;
  delivery_sales_tax_excluded: number;
  cost_rate: number | null;
  gross_margin_rate: number | null;
  labor_rate: number | null;
  opex_rate: number | null;
  customer_count: number;
  visitor_count_method: 'menu_derive' | 'manual_input' | 'external_sync' | null;
  avg_price_tax_excluded: number | null;
  thresholds: Record<string, ThresholdEntry>;
};

/** KPI種別 (kpi_threshold_master.kpi_type) の日本語ラベル。警告バナーの文言生成に使用 */
const KPI_TYPE_LABELS: Record<string, string> = {
  cost_rate: '原価率',
  gross_margin: '粗利率',
  labor_rate: '人件費率',
  opex_rate: '諸経費率',
  sales: '売上高',
  customer_count: '来客数',
  avg_price: '平均単価',
};

/** KpiCard 用の方向別アラート文言 (above=超過, below=未達)。閾値未設定 or 未超過時は undefined */
const alertLabel = (store: StoreKpi, kpiType: string): string | undefined => {
  const t = store.thresholds[kpiType];
  if (!t?.alert) return undefined;
  return t.direction === 'above' ? '閾値超過' : '閾値未達';
};

type OverviewResponse = {
  period: { start: string; end: string; label: string };
  stores: StoreKpi[];
  prev_month?: { label: string; stores: StoreKpi[] } | null;
  publish_gate?: PublishGateInfo;
};

const buildMonthOptions = (): string[] => {
  const opts: string[] = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`);
  }
  return opts;
};

type CategoryRow = {
  menu_category_id: string | null;
  category_name: string | null;
  total_qty: number;
  total_sales_tax_excluded: number;
};

type MenuRow = {
  menu_id: string;
  menu_code: string;
  menu_name: string;
  menu_category_id: string | null;
  total_qty: number;
  total_sales_tax_excluded: number;
};

type CostCategoryRow = {
  cost_category_id: string | null;
  category_name: string | null;
  total_cost_tax_excluded: number;
};

type CostItemRow = {
  ingredient_name: string;
  total_qty: number | null;
  total_cost_tax_excluded: number;
};

type OpexItemRow = {
  item_name: string;
  total_cost_tax_excluded: number;
};

type OpexItemsResponse = {
  items: OpexItemRow[];
  publish_gate?: PublishGateInfo;
};

/** 表示用パーセンテージ計算（分母0時は0%） */
const pct = (part: number, total: number): number => (total > 0 ? part / total : 0);

/** 割合の視覚バー用 */
const bar = (rate: number) => (
  <div className="h-2 bg-neutral-line rounded-full overflow-hidden">
    <div className="h-full bg-brand-red" style={{ width: `${(rate * 100).toFixed(1)}%` }} />
  </div>
);

// ============================================================================
// メニューカテゴリ別表（ドリルダウン付き）
// ============================================================================
function MenuCategoryTable({
  storeId, periodStart, categories,
}: {
  storeId: string; periodStart: string; categories: CategoryRow[];
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const total = categories.reduce((s, c) => s + (c.total_sales_tax_excluded ?? 0), 0);

  return (
    <section className="border border-neutral-line rounded-lg bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-neutral-line bg-neutral-panel">
        <h2 className="text-h3">メニューカテゴリ別売上</h2>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full min-w-[420px] sm:min-w-[560px] text-sm">
        <thead className="text-neutral-sub bg-neutral-panel/50">
          <tr>
            <th className="sm:sticky sm:left-0 z-20 bg-neutral-panel text-left px-2 sm:px-4 py-2 w-6 sm:w-8"></th>
            <th className="sm:sticky sm:left-6 md:left-8 z-20 bg-neutral-panel sm:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)] text-left px-2 sm:px-4 py-2 min-w-[5rem] sm:min-w-[7rem]">カテゴリ</th>
            <th className="text-right px-2 sm:px-4 py-2 w-14 sm:w-16 whitespace-nowrap">数量</th>
            <th className="text-right px-2 sm:px-4 py-2 w-24 sm:w-28 whitespace-nowrap">売上（税抜）</th>
            <th className="text-right px-2 sm:px-4 py-2 w-12 sm:w-14 whitespace-nowrap">割合</th>
            <th className="hidden md:table-cell text-left px-4 py-2 w-32">構成比</th>
          </tr>
        </thead>
        <tbody>
          {categories.map((r) => {
            const rate = pct(r.total_sales_tax_excluded, total);
            const isOpen = expanded === r.menu_category_id;
            return (
              <React.Fragment key={r.menu_category_id ?? 'null'}>
                <tr
                  className={`group border-t border-neutral-line cursor-pointer hover:bg-neutral-panel ${isOpen ? 'bg-neutral-panel' : ''}`}
                  onClick={() => setExpanded(isOpen ? null : (r.menu_category_id ?? null))}
                >
                  <td className={`sm:sticky sm:left-0 z-10 px-2 sm:px-4 py-2 text-neutral-sub ${isOpen ? 'bg-neutral-panel' : 'bg-white group-hover:bg-neutral-panel'}`}>
                    {r.menu_category_id ? (isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />) : null}
                  </td>
                  <td className={`sm:sticky sm:left-6 md:left-8 z-10 px-2 sm:px-4 py-2 font-medium ${isOpen ? 'bg-neutral-panel' : 'bg-white group-hover:bg-neutral-panel'}`}>{r.category_name ?? '未分類'}</td>
                  <td className="px-2 sm:px-4 py-2 text-right tabular-nums whitespace-nowrap">{formatCount(r.total_qty)}</td>
                  <td className="px-2 sm:px-4 py-2 text-right tabular-nums whitespace-nowrap">{formatCurrency(r.total_sales_tax_excluded)}</td>
                  <td className="px-2 sm:px-4 py-2 text-right tabular-nums whitespace-nowrap">{formatPercent(rate)}</td>
                  <td className="hidden md:table-cell px-4 py-2">{bar(rate)}</td>
                </tr>
                {isOpen && r.menu_category_id && (
                  <MenuDetailRows
                    storeId={storeId}
                    periodStart={periodStart}
                    menuCategoryId={r.menu_category_id}
                    categoryTotal={r.total_sales_tax_excluded}
                  />
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      </div>
    </section>
  );
}

function MenuDetailRows({
  storeId, periodStart, menuCategoryId, categoryTotal,
}: {
  storeId: string; periodStart: string; menuCategoryId: string; categoryTotal: number;
}) {
  const { data, isLoading } = useQuery<{ menus: MenuRow[] }>({
    queryKey: ['dashboard', 'menus', storeId, periodStart, menuCategoryId],
    queryFn: () => apiFetch<{ menus: MenuRow[] }>(
      `/dashboard/stores/${storeId}/menus?period_start=${periodStart}&menu_category_id=${menuCategoryId}`,
    ),
  });

  if (isLoading) return (<tr><td colSpan={6} className="px-4 py-2 text-neutral-sub text-xs">読み込み中…</td></tr>);
  const menus = data?.menus ?? [];
  if (menus.length === 0) return (<tr><td colSpan={6} className="px-4 py-2 text-neutral-sub text-xs">明細なし</td></tr>);

  return (
    <>
      {menus.map((m) => {
        const rate = pct(m.total_sales_tax_excluded, categoryTotal);
        return (
          <tr key={m.menu_id} className="border-t border-neutral-line bg-white text-xs">
            <td className="sm:sticky sm:left-0 z-10 bg-white"></td>
            <td className="sm:sticky sm:left-6 md:left-8 z-10 bg-white pl-6 sm:pl-10 pr-2 sm:pr-4 py-1.5 text-neutral-sub">└ {m.menu_name}</td>
            <td className="px-2 sm:px-4 py-1.5 text-right tabular-nums text-neutral-sub whitespace-nowrap">{formatCount(m.total_qty)}</td>
            <td className="px-2 sm:px-4 py-1.5 text-right tabular-nums text-neutral-sub whitespace-nowrap">{formatCurrency(m.total_sales_tax_excluded)}</td>
            <td className="px-2 sm:px-4 py-1.5 text-right tabular-nums text-neutral-sub whitespace-nowrap">{formatPercent(rate)}</td>
            <td className="hidden md:table-cell px-4 py-1.5">{bar(rate)}</td>
          </tr>
        );
      })}
    </>
  );
}

// ============================================================================
// コスト分類別表（ドリルダウン付き）
// ============================================================================
function CostCategoryTable({
  storeId, periodStart, categories,
}: {
  storeId: string; periodStart: string; categories: CostCategoryRow[];
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const total = categories.reduce((s, c) => s + (c.total_cost_tax_excluded ?? 0), 0);

  return (
    <section className="border border-neutral-line rounded-lg bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-neutral-line bg-neutral-panel">
        <h2 className="text-h3">コスト分類別原価</h2>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full min-w-[380px] sm:min-w-[480px] text-sm">
        <thead className="text-neutral-sub bg-neutral-panel/50">
          <tr>
            <th className="sm:sticky sm:left-0 z-20 bg-neutral-panel text-left px-2 sm:px-4 py-2 w-6 sm:w-8"></th>
            <th className="sm:sticky sm:left-6 md:left-8 z-20 bg-neutral-panel sm:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)] text-left px-2 sm:px-4 py-2 min-w-[5rem] sm:min-w-[7rem]">分類</th>
            <th className="text-right px-2 sm:px-4 py-2 w-24 sm:w-28 whitespace-nowrap">原価（税抜）</th>
            <th className="text-right px-2 sm:px-4 py-2 w-12 sm:w-14 whitespace-nowrap">割合</th>
            <th className="hidden md:table-cell text-left px-4 py-2 w-32">構成比</th>
          </tr>
        </thead>
        <tbody>
          {categories.map((r) => {
            const rate = pct(r.total_cost_tax_excluded, total);
            const isOpen = expanded === r.cost_category_id;
            return (
              <React.Fragment key={r.cost_category_id ?? 'null'}>
                <tr
                  className={`group border-t border-neutral-line cursor-pointer hover:bg-neutral-panel ${isOpen ? 'bg-neutral-panel' : ''}`}
                  onClick={() => setExpanded(isOpen ? null : (r.cost_category_id ?? null))}
                >
                  <td className={`sm:sticky sm:left-0 z-10 px-2 sm:px-4 py-2 text-neutral-sub ${isOpen ? 'bg-neutral-panel' : 'bg-white group-hover:bg-neutral-panel'}`}>
                    {r.cost_category_id ? (isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />) : null}
                  </td>
                  <td className={`sm:sticky sm:left-6 md:left-8 z-10 px-2 sm:px-4 py-2 font-medium ${isOpen ? 'bg-neutral-panel' : 'bg-white group-hover:bg-neutral-panel'}`}>{r.category_name ?? '未分類'}</td>
                  <td className="px-2 sm:px-4 py-2 text-right tabular-nums whitespace-nowrap">{formatCurrency(r.total_cost_tax_excluded)}</td>
                  <td className="px-2 sm:px-4 py-2 text-right tabular-nums whitespace-nowrap">{formatPercent(rate)}</td>
                  <td className="hidden md:table-cell px-4 py-2">{bar(rate)}</td>
                </tr>
                {isOpen && r.cost_category_id && (
                  <CostItemRows
                    storeId={storeId}
                    periodStart={periodStart}
                    costCategoryId={r.cost_category_id}
                    categoryTotal={r.total_cost_tax_excluded}
                  />
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      </div>
    </section>
  );
}

function CostItemRows({
  storeId, periodStart, costCategoryId, categoryTotal,
}: {
  storeId: string; periodStart: string; costCategoryId: string; categoryTotal: number;
}) {
  const { data, isLoading } = useQuery<{ items: CostItemRow[] }>({
    queryKey: ['dashboard', 'cost-items', storeId, periodStart, costCategoryId],
    queryFn: () => apiFetch<{ items: CostItemRow[] }>(
      `/dashboard/stores/${storeId}/cost-items?period_start=${periodStart}&cost_category_id=${costCategoryId}`,
    ),
  });

  if (isLoading) return (<tr><td colSpan={5} className="px-4 py-2 text-neutral-sub text-xs">読み込み中…</td></tr>);
  const items = data?.items ?? [];
  if (items.length === 0) return (<tr><td colSpan={5} className="px-4 py-2 text-neutral-sub text-xs">明細なし</td></tr>);

  return (
    <>
      {items.slice(0, 30).map((it) => {
        const rate = pct(it.total_cost_tax_excluded, categoryTotal);
        return (
          <tr key={it.ingredient_name} className="border-t border-neutral-line bg-white text-xs">
            <td className="sm:sticky sm:left-0 z-10 bg-white"></td>
            <td className="sm:sticky sm:left-6 md:left-8 z-10 bg-white pl-6 sm:pl-10 pr-2 sm:pr-4 py-1.5 text-neutral-sub">└ {it.ingredient_name}</td>
            <td className="px-2 sm:px-4 py-1.5 text-right tabular-nums text-neutral-sub whitespace-nowrap">{formatCurrency(it.total_cost_tax_excluded)}</td>
            <td className="px-2 sm:px-4 py-1.5 text-right tabular-nums text-neutral-sub whitespace-nowrap">{formatPercent(rate)}</td>
            <td className="hidden md:table-cell px-4 py-1.5">{bar(rate)}</td>
          </tr>
        );
      })}
      {items.length > 30 && (
        <tr><td colSpan={5} className="px-4 py-2 text-neutral-sub text-xs">…他 {items.length - 30} 件</td></tr>
      )}
    </>
  );
}

// ============================================================================
// 諸経費内訳表 (item_name フラット一覧、PL構造ツリーと同じ粒度)
// ============================================================================
function OpexItemTable({ items }: { items: OpexItemRow[] }) {
  const total = items.reduce((s, i) => s + (i.total_cost_tax_excluded ?? 0), 0);
  return (
    <section className="border border-neutral-line rounded-lg bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-neutral-line bg-neutral-panel">
        <h2 className="text-h3">諸経費内訳</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[380px] sm:min-w-[480px] text-sm">
          <thead className="text-neutral-sub bg-neutral-panel/50">
            <tr>
              <th scope="col" className="sm:sticky sm:left-0 z-20 bg-neutral-panel sm:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)] text-left px-2 sm:px-4 py-2 min-w-[7rem]">項目</th>
              <th scope="col" className="text-right px-2 sm:px-4 py-2 w-24 sm:w-28 whitespace-nowrap">諸経費（税抜）</th>
              <th scope="col" className="text-right px-2 sm:px-4 py-2 w-12 sm:w-14 whitespace-nowrap">割合</th>
              <th scope="col" className="hidden md:table-cell text-left px-4 py-2 w-32">構成比</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-3 text-neutral-sub text-xs">諸経費データがありません</td>
              </tr>
            ) : (
              items.map((r, idx) => {
                const rate = pct(r.total_cost_tax_excluded, total);
                // SQL 側で display_label で GROUP BY 済みのため item_name は基本一意だが、
                // 万一の重複でも React key 衝突を避けるため index を付与 (Design MEDIUM-4)
                return (
                  <tr key={`${r.item_name}-${idx}`} className="border-t border-neutral-line">
                    <td className="sm:sticky sm:left-0 z-10 bg-white px-2 sm:px-4 py-2 font-medium">{r.item_name}</td>
                    <td className="px-2 sm:px-4 py-2 text-right tabular-nums whitespace-nowrap">{formatCurrency(r.total_cost_tax_excluded)}</td>
                    <td className="px-2 sm:px-4 py-2 text-right tabular-nums whitespace-nowrap">{formatPercent(rate)}</td>
                    <td className="hidden md:table-cell px-4 py-2">{bar(rate)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ============================================================================
// StoreDashboardPage 本体
// ============================================================================
export function StoreDashboardPage() {
  const rootLabel = useRootLabel();
  const { storeId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const monthOptions = buildMonthOptions();
  // 5-1 (docs/design/v05-dashboard-scope-pinning.md §0-2-4): URLに period_start が無い場合の
  // フォールバックのみ変更。Overview発の遷移リンクは既に?period_start=を付けているため、
  // Overview側が固定値を初期表示していれば自動的に伝播する (このページ自体の追加改修は不要)。
  // 本値はuseStateではなく毎レンダー導出のため§0-2-2のstale値問題は生じない。
  const isPinned = useScopePinStore((s) => s.isPinned);
  const pinnedPeriodEnd = useScopePinStore((s) => s.pinnedPeriodEnd);
  // 固定した月が生成レンジ(直近24ヶ月)より古い場合に<select>のvalueが一覧に無く表示が崩れるのを防ぐ
  // (実装レビューHIGH: DashboardOverviewPage/LiteDashboardPageと同じギャップ埋めが抜けていた)
  if (isPinned && pinnedPeriodEnd && !monthOptions.includes(pinnedPeriodEnd)) {
    monthOptions.push(pinnedPeriodEnd);
    monthOptions.sort().reverse();
  }
  const periodStart = searchParams.get('period_start') ?? (isPinned && pinnedPeriodEnd ? pinnedPeriodEnd : monthOptions[0]!);

  // v0.3 (S-13 staging E2Eで発見・修正): 本ページはURLの:storeIdで店舗が一意に決まる画面のため、
  // 常に当該店舗のnode-overviewを問い合わせる。グローバルなNodeSwitcherスコープ(selectedNodeId)を
  // 使うDashboardOverviewPageと同じ配線をコピーしていたため、NodeSwitcherで別店舗/全社を選択中の
  // ままこのページへ直接遷移すると publish_gate (未公開バナー等) が「表示中の店舗」ではなく
  // 「NodeSwitcherの選択」の情報になってしまっていた (KPI数値自体はoverview.data.storesからstoreIdで
  // 絞り込むため正しかったが、publish_gateはレスポンス直下のフィールドのため絞り込まれず食い違っていた)。
  const overview = useQuery<OverviewResponse>({
    queryKey: ['dashboard', 'overview', storeId, periodStart, 'compare=prev_month'],
    queryFn: () => apiFetch<OverviewResponse>(
      `/dashboard/nodes/${storeId}/overview?period_start=${periodStart}&compare=prev_month`,
    ),
    enabled: Boolean(storeId && periodStart),
  });

  const categories = useQuery<{ categories: CategoryRow[] }>({
    queryKey: ['dashboard', 'store-categories', storeId, periodStart],
    queryFn: () => apiFetch<{ categories: CategoryRow[] }>(
      `/dashboard/stores/${storeId}/categories?period_start=${periodStart}`,
    ),
    enabled: Boolean(storeId && periodStart),
  });

  const costCategories = useQuery<{ cost_categories: CostCategoryRow[] }>({
    queryKey: ['dashboard', 'store-cost-categories', storeId, periodStart],
    queryFn: () => apiFetch<{ cost_categories: CostCategoryRow[] }>(
      `/dashboard/stores/${storeId}/cost-categories?period_start=${periodStart}`,
    ),
    enabled: Boolean(storeId && periodStart),
  });

  const opexItems = useQuery<OpexItemsResponse>({
    queryKey: ['dashboard', 'store-opex-items', storeId, periodStart],
    queryFn: () => apiFetch<OpexItemsResponse>(
      `/dashboard/stores/${storeId}/opex-items?period_start=${periodStart}`,
    ),
    enabled: Boolean(storeId && periodStart),
  });

  const store = overview.data?.stores.find((s) => s.store_id === storeId);
  const prevStore = overview.data?.prev_month?.stores.find((s) => s.store_id === storeId) ?? null;
  const prevLabel = overview.data?.prev_month?.label ?? '前月';

  const onChangePeriod = (v: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('period_start', v);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      {/* パンくず */}
      <nav aria-label="パンくずリスト" className="flex items-center gap-1 text-sm text-neutral-sub">
        <Link to="/dashboard" className="inline-flex items-center gap-1 hover:text-brand-red">
          <Home size={14} />
          経営ダッシュボード（{rootLabel}）
        </Link>
        <ChevronRight size={14} />
        <span className="text-neutral-text font-medium">{store?.store_name ?? '店舗詳細'}</span>
      </nav>

      {/* 期間切替 */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-h1">{store?.store_name ?? '店舗詳細'}</h1>
          <p className="text-sm text-neutral-sub mt-1">{overview.data?.period.label ?? ''}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={`/dashboard/stores/${storeId}/pl-tree?period_start=${periodStart}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-neutral-line text-sm text-neutral-text hover:bg-neutral-panel"
          >
            PL構造ツリー
          </Link>
          <select
            value={periodStart}
            onChange={(e) => onChangePeriod(e.target.value)}
            className="px-3 py-1.5 rounded border border-neutral-line text-sm focus-ring"
            aria-label="対象月"
          >
            {monthOptions.map((m) => (
              <option key={m} value={m}>
                {m.slice(0, 4)}年{Number(m.slice(5, 7))}月
              </option>
            ))}
          </select>
        </div>
      </div>

      <PublishGateBanner publishGate={overview.data?.publish_gate} />

      {store ? (
        <>
          {(() => {
            const alerts = Object.entries(store.thresholds).filter(([, t]) => t.alert);
            if (alerts.length === 0) return null;
            return (
              <div className="p-3 rounded bg-warning-bg text-warning-text border border-warning-border text-sm flex items-start gap-2">
                <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                <div>
                  {alerts
                    .map(([kpiType, t]) => `${KPI_TYPE_LABELS[kpiType] ?? kpiType}が${t.direction === 'above' ? '閾値を超過しています' : '閾値を下回っています'}`)
                    .join('、')}
                </div>
              </div>
            );
          })()}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            <KpiCard
              title="売上高 (税抜)"
              value={store.sales_tax_excluded}
              format="currency"
              alertMessage={alertLabel(store, 'sales')}
              delta={prevStore ? { prevValue: prevStore.sales_tax_excluded, label: `${prevLabel}比` } : null}
            />
            <KpiCard
              title="粗利率"
              value={store.gross_margin_rate}
              format="percent"
              alertMessage={alertLabel(store, 'gross_margin')}
              delta={prevStore ? { prevValue: prevStore.gross_margin_rate, label: `${prevLabel}比` } : null}
            />
            <KpiCard
              title="原価率"
              value={store.cost_rate}
              format="percent"
              alertMessage={alertLabel(store, 'cost_rate')}
              delta={prevStore ? { prevValue: prevStore.cost_rate, label: `${prevLabel}比`, invert: true } : null}
            />
            <KpiCard
              title="人件費率"
              value={store.labor_rate}
              format="percent"
              alertMessage={alertLabel(store, 'labor_rate')}
              delta={prevStore ? { prevValue: prevStore.labor_rate, label: `${prevLabel}比`, invert: true } : null}
            />
            <KpiCard
              title="諸経費率"
              value={store.opex_rate}
              format="percent"
              alertMessage={alertLabel(store, 'opex_rate')}
              delta={prevStore ? { prevValue: prevStore.opex_rate, label: `${prevLabel}比`, invert: true } : null}
            />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <KpiCard
              title="来客数"
              value={store.customer_count}
              format="count"
              unit=" 人"
              alertMessage={alertLabel(store, 'customer_count')}
              sourceBadge={visitorCountSourceBadge(store.visitor_count_method)}
              delta={prevStore ? { prevValue: prevStore.customer_count, label: `${prevLabel}比` } : null}
            />
            <KpiCard
              title="平均単価"
              value={store.avg_price_tax_excluded}
              format="currency"
              alertMessage={alertLabel(store, 'avg_price')}
              delta={prevStore ? { prevValue: prevStore.avg_price_tax_excluded, label: `${prevLabel}比` } : null}
            />
          </div>

          {/* userフィードバック(2026-09-08本番実機): 店舗詳細画面に入ると売上の内訳(店舗/デリバリー)が
              見えなかった。StoreKpi型には既にstore_sales_tax_excluded/delivery_sales_tax_excludedが
              あったがJSXで未使用だった (DashboardOverviewPage.tsxの③と同じ内容をここにも追加する) */}
          {(() => {
            const total = store.store_sales_tax_excluded + store.delivery_sales_tax_excluded;
            const storePct = total > 0 ? (store.store_sales_tax_excluded / total) * 100 : 0;
            const deliveryPct = total > 0 ? 100 - storePct : 0;
            return (
              <div className="border border-neutral-line rounded-lg bg-white p-4">
                <h4 className="text-sm font-semibold mb-2">店舗・デリバリー内訳（税抜）</h4>
                <div className="flex h-4 rounded overflow-hidden bg-neutral-line">
                  <div style={{ width: `${storePct}%` }} className="bg-chart-1" />
                  <div style={{ width: `${deliveryPct}%` }} className="bg-chart-3" />
                </div>
                <div className="flex items-center justify-between text-xs text-neutral-sub mt-1 tabular-nums">
                  <span>店舗 {formatCurrency(store.store_sales_tax_excluded)}（{storePct.toFixed(0)}%）</span>
                  <span>デリバリー {formatCurrency(store.delivery_sales_tax_excluded)}（{deliveryPct.toFixed(0)}%）</span>
                </div>
              </div>
            );
          })()}

          {storeId && periodStart && <AiPeriodCommentPanel nodeId={storeId} targetMonth={periodStart.slice(0, 7)} />}
          {storeId && periodStart && <DashboardNodeMemo nodeId={storeId} targetMonth={periodStart.slice(0, 7)} />}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {categories.data && (
              <MenuCategoryTable
                storeId={storeId}
                periodStart={periodStart}
                categories={categories.data.categories}
              />
            )}
            {costCategories.data && (
              <CostCategoryTable
                storeId={storeId}
                periodStart={periodStart}
                categories={costCategories.data.cost_categories}
              />
            )}
            {opexItems.data && (
              <OpexItemTable items={opexItems.data.items} />
            )}
          </div>

          <p className="text-xs text-neutral-sub">※ カテゴリ行をクリックすると個別内訳が展開されます</p>
        </>
      ) : overview.isLoading ? (
        <div className="text-neutral-sub">読み込み中…</div>
      ) : overview.isError ? (
        <div className="p-3 rounded bg-warning-bg text-warning-text border border-warning-border">
          データ取得に失敗しました：{(overview.error as Error).message}
        </div>
      ) : (
        <div className="p-6 text-center text-neutral-sub border border-neutral-line rounded bg-neutral-panel">
          店舗が見つかりません（アクセス権限外の可能性があります）
        </div>
      )}
    </div>
  );
}
